import React, { useState, useEffect, useMemo } from 'react'
import Swal from 'sweetalert2'
import Select from 'react-select'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const ReturntoWarehouse = () => {
  const [formData, setFormData] = useState({
    source_branch_id: '',
    notes: ''
  })
  const [items, setItems] = useState([
    { product_id: '', quantity: '', notes: '' }
  ])
  const [branches, setBranches] = useState([])
  const [products, setProducts] = useState([])
  const [mainWarehouse, setMainWarehouse] = useState(null)
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchingData(true)
        const [warehousesResponse, branchesResponse, productsResponse] = await Promise.all([
          apiClient.get(API_ENDPOINTS.WAREHOUSES),
          apiClient.get(API_ENDPOINTS.BRANCHES),
          apiClient.get('/api/products/products/')
        ])
        
        const warehousesData = warehousesResponse.data?.results || warehousesResponse.data || []
        setBranches(branchesResponse.data?.results || branchesResponse.data || [])
        setProducts(productsResponse.data || [])
        
        // Find main warehouse
        const main = warehousesData.find(w => w.is_main === true)
        if (main) {
          setMainWarehouse(main)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load warehouses, branches, or products. Please refresh the page.'
        })
      } finally {
        setFetchingData(false)
      }
    }

    fetchData()
  }, [])

  // Transform data for react-select
  const branchOptions = useMemo(() => {
    return branches.map(branch => ({
      value: branch.id,
      label: `${branch.name} (${branch.warehouse_name || 'N/A'})`
    }))
  }, [branches])

  const productOptions = useMemo(() => {
    return products.map(product => ({
      value: product.id,
      label: `${product.name} (${product.sku})`
    }))
  }, [products])

  // Get selected values for react-select
  const selectedSourceBranch = useMemo(() => {
    return formData.source_branch_id 
      ? branchOptions.find(opt => opt.value === parseInt(formData.source_branch_id))
      : null
  }, [formData.source_branch_id, branchOptions])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value === '' ? '' : value
    })
  }

  const handleSelectChange = (selectedOption, fieldName) => {
    const newFormData = { ...formData }
    newFormData[fieldName] = selectedOption ? selectedOption.value.toString() : ''
    setFormData(newFormData)
  }

  const handleItemChange = (index, field, value) => {
    const newItems = [...items]
    newItems[index] = {
      ...newItems[index],
      [field]: value === '' ? '' : value
    }
    setItems(newItems)
  }

  const handleItemSelectChange = (index, selectedOption, fieldName) => {
    const newItems = [...items]
    newItems[index] = {
      ...newItems[index],
      [fieldName]: selectedOption ? selectedOption.value.toString() : ''
    }
    setItems(newItems)
  }

  const addItem = () => {
    setItems([...items, { product_id: '', quantity: '', notes: '' }])
  }

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Validate main warehouse exists
      if (!mainWarehouse) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'No main warehouse found. Please ensure a main warehouse is configured.'
        })
        setLoading(false)
        return
      }

      // Validate source branch
      if (!formData.source_branch_id) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please select a source branch.'
        })
        setLoading(false)
        return
      }

      // Validate items
      const validItems = items.filter(item => 
        item.product_id && item.quantity
      )

      if (validItems.length === 0) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please add at least one product with quantity.'
        })
        setLoading(false)
        return
      }

      // Build payload according to new API structure
      const transferPayload = {
        transfer_type: 'branch_to_warehouse',
        source_branch_id: parseInt(formData.source_branch_id),
        destination_warehouse_id: mainWarehouse.id,
        items: validItems.map(item => {
          const transferItem = {
            product_id: parseInt(item.product_id),
            quantity: parseFloat(item.quantity)
          }
          
          if (item.notes) {
            transferItem.notes = item.notes
          }
          
          return transferItem
        })
      }

      if (formData.notes) {
        transferPayload.notes = formData.notes
      }

      // Call single-transfer endpoint
      const response = await apiClient.post('/api/stock/transfers/', transferPayload)

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: response.data?.reference_number
          ? `Stock transfer ${response.data.reference_number} created successfully!`
          : 'Stock transfer created successfully!',
        confirmButtonText: 'OK'
      })

      // Reset form
      setFormData({
        source_branch_id: '',
        notes: ''
      })
      setItems([{ product_id: '', quantity: '', notes: '' }])
    } catch (error) {
      console.error('Error creating stock transfer:', error)
      
      let errorMessage = 'Failed to create stock transfer. Please try again.'
      
      if (error.response?.data) {
        const errorData = error.response.data
        
        if (errorData.detail) {
          errorMessage = errorData.detail
        } else if (errorData.non_field_errors) {
          errorMessage = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors[0] : errorData.non_field_errors
        } else if (errorData.message) {
          errorMessage = errorData.message
        } else {
          // Get first field error
          const firstError = Object.values(errorData)[0]
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
        }
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }


  return (
    <>
      <div className="pagetitle">
        <h1>Request Stock</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Products</li>
            <li className="breadcrumb-item active">Request Stocks</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-arrow-left-right me-2"></i>
                  Request Stocks
                </h5>

                <form onSubmit={handleSubmit}>
                  <div className="row mb-3">
                    <label htmlFor="source_branch_id" className="col-sm-2 col-form-label">
                      Source Branch <span className="text-danger">*</span>
                    </label>
                    <div className="col-sm-10">
                      <Select
                        id="source_branch_id"
                        name="source_branch_id"
                        value={selectedSourceBranch}
                        onChange={(option) => handleSelectChange(option, 'source_branch_id')}
                        options={branchOptions}
                        isClearable
                        isSearchable
                        placeholder="Select Source Branch"
                        isDisabled={loading || fetchingData}
                        required
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            borderColor: '#ced4da',
                            '&:hover': {
                              borderColor: '#ced4da'
                            }
                          })
                        }}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">
                      Destination Warehouse
                    </label>
                    <div className="col-sm-10">
                      <input
                        type="text"
                        className="form-control"
                        value={mainWarehouse ? `${mainWarehouse.name} (Main Warehouse)` : 'Loading...'}
                        disabled
                        readOnly
                      />
                      <small className="form-text text-muted">
                        Products will be returned to the main warehouse automatically.
                      </small>
                    </div>
                  </div>

                  {/* Multi-Product Transfer */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Products</label>
                    <div className="col-sm-10">
                      {items.map((item, index) => (
                        <div key={index} className="card mb-3">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="card-title mb-0">Product {index + 1}</h6>
                              {items.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => removeItem(index)}
                                  disabled={loading || fetchingData}
                                >
                                  <i className="bi bi-trash"></i> Remove
                                </button>
                              )}
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Product <span className="text-danger">*</span>
                              </label>
                              <div className="col-sm-9">
                                <Select
                                  value={item.product_id ? productOptions.find(opt => opt.value === parseInt(item.product_id)) : null}
                                  onChange={(option) => handleItemSelectChange(index, option, 'product_id')}
                                  options={productOptions}
                                  isClearable
                                  isSearchable
                                  placeholder="Select Product"
                                  isDisabled={loading || fetchingData}
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '38px',
                                      borderColor: '#ced4da',
                                      '&:hover': {
                                        borderColor: '#ced4da'
                                      }
                                    })
                                  }}
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Quantity <span className="text-danger">*</span>
                              </label>
                              <div className="col-sm-9">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="form-control"
                                  value={item.quantity}
                                  onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                  placeholder="Enter quantity"
                                  disabled={loading || fetchingData}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <label className="col-sm-3 col-form-label">Notes</label>
                              <div className="col-sm-9">
                                <textarea
                                  className="form-control"
                                  rows="2"
                                  value={item.notes}
                                  onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                                  placeholder="Enter notes (Optional)"
                                  disabled={loading || fetchingData}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={addItem}
                        disabled={loading || fetchingData}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add Another Product
                      </button>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="notes" className="col-sm-2 col-form-label">Transfer Notes</label>
                    <div className="col-sm-10">
                      <textarea
                        className="form-control"
                        id="notes"
                        name="notes"
                        rows="3"
                        value={formData.notes}
                        onChange={handleChange}
                        placeholder="Enter transfer notes (Optional)"
                        disabled={loading || fetchingData}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-sm-10 offset-sm-2">
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={loading || fetchingData}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating Transfer...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-arrow-left-right me-2"></i>
                            Return To Warehouse
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}




export default ReturntoWarehouse
